import { runInAction } from 'mobx'
import { getRoomState } from '../../firestore/RoomState'
import {
  getRoomStateAnswers,
  getRoomStateAnswersForGroup,
} from '../../firestore/RoomStateAnswer'
import { getRoomStateFeedback } from '../../firestore/RoomStateFeedback'
import { getRoomStateSummary } from '../../firestore/RoomStateSummary'
import { getSection } from '../../firestore/Section'
import { fetchSectionAssignment } from '../../firestore/SectionAssignment'
import { fetchSettingsProcessingVideo } from '../../firestore/SettingsProcessingVideo'
import { getSettingsDefaultProcessingAsset } from '../../firestore/SettingsDefaultProcessingAsset'
import { fetchMedia } from '../../firestore/Media'
import { fetchSlides, getSlides } from '../../firestore/Slide'
import { fetchSlideCaptions } from '../../firestore/SlideCaption'
import { fetchSlideDeck, getSlideDeck } from '../../firestore/SlideDeck'
import {
  fetchSlideDeckExhibits,
  getSlideDeckExhibits,
} from '../../firestore/SlideDeckExhibit'
import {
  fetchSlideQuestions,
  getSlideQuestions,
} from '../../firestore/SlideQuestion'
import { getPeerReviewQuestionsStream } from '../../firestore/SectionAssignmentPeerReviewQuestion'
import { getUserProfileRoomToken } from '../../firestore/UserProfileRoomToken'
import type { BreakoutUser } from '../../models/BreakoutUser'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { Media } from '../../models/Media'
import type { RoomState } from '../../models/RoomState'
import { RoomStateAnswer } from '../../models/RoomStateAnswer'
import { WrappedFirestoreError } from '../../types'
import type { MeetingCubit } from '../MeetingCubit'
import { Cubit } from '../core'
import { getRoomStateCollaborationData } from '../../firestore/RoomStateCollaborationData'
import { getSettingsSectionPassPricing } from '../../firestore/SettingsSectionPassPricing'
import { fetchSettingsTermsAndConditions } from '../../firestore/SettingsTermsAndConditons.ts'
import { fetchClickWrap } from '../../firestore/ClickWrap/index.ts'
import {
  fetchSlideDeckRoles,
  getSlideDeckRoles,
} from '../../firestore/SlideDeckRole/index.ts'

/**
 * The MeetingDataFetcher class is responsible for fetching data from Firestore
 * and updating the Meeting model with the fetched data.
 *
 * The idea is to break up the Meeting class into smaller, more manageable pieces.
 */
export class MeetingDataFetcher extends Cubit {
  repository: FirebaseRepository
  currentUser: BreakoutUser
  useStreams: boolean = false
  _fetchedSection = false
  constructor(
    protected meeting: MeetingCubit,
    options: {
      useStreams?: boolean
    } = {}
  ) {
    super()
    this.repository = meeting.repository
    this.currentUser = meeting.currentUser
    this.useStreams = options.useStreams ?? false
  }

  started: boolean = false
  start() {
    if (this.started) {
      console.warn('MeetingDataFetcher already started, skipping start.')
      return
    }
    this.started = true

    this.addStream(
      getRoomState(this.repository, { roomStateId: this.meeting.roomId }),
      async (newRoomState) => {
        if (
          newRoomState.data === undefined &&
          this.meeting.roomState.isLoading
        ) {
          this.meeting.markRoomFailedToLoad()
          return
        }

        if (newRoomState.data === undefined) return

        // this.meeting.sectionId is not set yet, so we get the id from the room state
        const sectionId = newRoomState.data.sectionId

        this.startSectionStream(sectionId)

        await this.meeting.updateRoomState(newRoomState)

        this.getRoomStateDependentData(newRoomState)

        this.meeting.onRoomReady()
      },
      {
        onError: (error) => {
          if (
            error instanceof WrappedFirestoreError &&
            this.meeting.roomState.isLoading
          ) {
            // Firestore errored on first load, this means we don't have access
            this.meeting.markRoomFailedToLoad()
          }
        },
      }
    )

    // we only need to subscribe to the group answers once
    this.addGroupAnswersStream()
    this.addLivekitTokenStream()
    this.fetchSettingsProcessingVideo()
    this.fetchDefaultProcessingAsset()
    this.fetchSettingsTermsAndConditions()
    this.addCaptionsReaction()
  }

  startSectionStream(sectionId: string | undefined) {
    if (!sectionId) return

    this.removeStream('section')

    this.addStream(
      getSection(this.repository, { sectionId }),
      (section) => {
        this.meeting.section.replaceModel(section)

        const sectionPassKey = 'section-pass-pricing'

        this.removeStream(sectionPassKey)

        this.addStream(
          getSettingsSectionPassPricing(this.repository),
          (pricing) => {
            this.meeting.sectionPassPricing.replaceModel(pricing)
          },
          {
            name: sectionPassKey,
          }
        )
      },
      {
        name: 'section',
      }
    )
  }

  addCaptionsReaction() {
    this.addReaction({
      whenThisChanges: () => this.meeting.roomState.data.activeSlide,
      thenRunThisCode: () => this.resetCaptions(),
    })
  }

  async resetCaptions() {
    this.meeting.slideCaptions.replaceModels([])

    const slideId = this.meeting.currentSlideId
    const slide = this.meeting.slideDeckSlides.models.find(
      (slide) => slide.id === slideId
    )
    if (slide) {
      const newCaptions = await fetchSlideCaptions(this.meeting.repository, {
        slideDeckId: this.meeting.slideDeckId,
        slide: slide,
      })

      this.meeting.slideCaptions.replaceModels(newCaptions)
    }
  }

  async getRoomStateDependentData(roomState: RoomState) {
    if (this.useStreams) {
      this.addSlideDeckStream()
      this.addRolesStream()
      this.addExhibitsStream()
      this.addSlidesStream()
      this.addQuestionsStream()
    } else {
      // These are related ot the slide deck and they are immutable
      // so we only need to fetch them once
      this.fetchSlideDeck(roomState.data.slideDeckId)
      this.fetchRoles(roomState.data.slideDeckId)
      this.fetchExhibits(roomState.data.slideDeckId)
      this.fetchSlides(roomState.data.slideDeckId)
      this.fetchQuestions(roomState.data.slideDeckId)
      this.fetchAssignment(
        roomState.data.assignmentId,
        roomState.data.sectionId
      )
    }

    this.addUserAnswersStreams()
    this.addRoomStateFeedbackStream()
    this.addRoomStateSummaryStream()
    this.addRoomStateEngagementStream()
    this.addRoomStateCollaborationDataStream()
    this.addPeerReviewQuestionsStream(roomState)
  }

  addSlideDeckStream() {
    if (this.hasStream('slideDeck')) return
    this.addStream(
      getSlideDeck(this.repository, {
        slideDeckId: this.meeting.slideDeckId,
      }),
      (slideDeck) => {
        // const wasEmpty = this.meeting.slideDeck.isEmpty

        this.meeting.slideDeck.replaceModel(slideDeck)

        // if (!wasEmpty) this.meeting.devModePumpSlide()
      },
      {
        name: 'slideDeck',
      }
    )
  }

  addExhibitsStream() {
    if (this.hasStream('slideDeckExhibits')) return
    this.addStream(
      getSlideDeckExhibits(this.repository, {
        slideDeckId: this.meeting.slideDeckId,
      }),
      (exhibits) => {
        // const wasEmpty = this.meeting.slideDeckExhibits.isEmpty

        this.meeting._slideDeckExhibits.replaceModels(exhibits)

        // if (!wasEmpty) this.meeting.devModePumpSlide()
      },
      {
        name: 'slideDeckExhibits',
      }
    )
  }

  addQuestionsStream() {
    if (this.hasStream('slideDeckQuestions')) return
    this.addStream(
      getSlideQuestions(this.repository, {
        slideDeckId: this.meeting.slideDeckId,
      }),
      (questions) => {
        // const wasEmpty = this.meeting.slideDeckQuestions.isEmpty

        // we replace the questions in the meeting model
        this.meeting.slideDeckQuestions.replaceModels(questions)

        // if (!wasEmpty) this.meeting.devModePumpSlide()
      },
      {
        name: 'slideDeckQuestions',
      }
    )
  }

  addSlidesStream() {
    if (this.hasStream('slideDeckSlides')) return

    this.addStream(
      getSlides(this.repository, {
        slideDeckId: this.meeting.slideDeckId,
      }),
      (slides) => {
        // const wasEmpty = this.meeting.slideDeckQuestions.isEmpty

        this.meeting.slideDeckSlides.replaceModels(slides)

        // if (!wasEmpty) this.meeting.devModePumpSlide()
      },
      {
        name: 'slideDeckSlides',
      }
    )
  }

  async addLivekitTokenStream() {
    this.addStream(
      getUserProfileRoomToken(this.repository, {
        userId: this.currentUser.uid,
        roomId: this.meeting.roomId,
      }),
      (roomToken) => {
        this.meeting.roomToken.replaceModel(roomToken)
      }
    )
  }

  async fetchSettingsProcessingVideo() {
    const setting = await fetchSettingsProcessingVideo(this.meeting.repository)

    this.meeting.settingsProcessingVideo.replaceModel(setting)
  }

  async fetchDefaultProcessingAsset() {
    try {
      this.addStream(
        getSettingsDefaultProcessingAsset(this.repository),
        (setting) => {
          this.meeting.defaultProcessingAsset.replaceModel(setting)

          if (setting.data === undefined) return

          // If a mediaId is set, fetch the corresponding Media document
          const mediaId = setting.data.mediaId
          if (mediaId && typeof mediaId === 'string' && mediaId !== '') {
            fetchMedia(this.meeting.repository, {
              mediaId: mediaId,
            })
              .then((media) => {
                this.meeting.defaultProcessingMedia.replaceModel(media)
              })
              .catch((error) => {
                console.error('Error fetching default processing media:', error)
                // Leave defaultProcessingMedia in its current state - will fall back to legacy
              })
          } else {
            // Clear the media if mediaId is empty/unset to prevent stale data
            this.meeting.defaultProcessingMedia.replaceModel(
              Media.empty(this.repository)
            )
          }
        },
        {
          name: 'settingsDefaultProcessingAsset',
        }
      )
    } catch (error) {
      console.error('Error fetching default processing asset:', error)
      // If the settings document doesn't exist yet, that's okay - fall back to legacy
      // Silently continue - will use legacy settingsProcessingVideo instead
    }
  }

  async fetchSettingsTermsAndConditions() {
    const setting = await fetchSettingsTermsAndConditions(
      this.meeting.repository
    )
    this.meeting.preMeetingTermsAndConditions.replaceModel(setting)

    if (!setting.data.preMeetingClickWrapId) {
      this.meeting.preMeetingClickWrap.changeLoadingState(false)
      return
    }
    const clickWrap = await fetchClickWrap(this.meeting.repository, {
      clickWrapId: setting.data.preMeetingClickWrapId,
    })
    this.meeting.preMeetingClickWrap.replaceModel(clickWrap)
  }

  addUserAnswersStreams() {
    const userIds = this.meeting.roomState.data.userIds
    for (const userId of userIds) {
      this.addUserAnswersStream(userId)
    }
  }

  addUserAnswersStream(userId: string) {
    const key = `userAnswers-${userId}`
    // once the stream is added, we don't need to add it again
    // we don't remove user streams as we very rarely (or never) remove users during the meeiting
    if (this.hasStream(key)) return

    this.addStream(
      getRoomStateAnswers(this.repository, {
        roomId: this.meeting.roomId,
        userId: userId,
      }),
      (answers) => {
        runInAction(() => {
          const found = this.meeting.roomStateAnswersPerUser.get(userId)
          if (found) {
            found.replaceModels(answers)
          } else {
            const empty = RoomStateAnswer.emptyCollection(this.repository)
            empty.replaceModels(answers)
            this.meeting.roomStateAnswersPerUser.set(userId, empty)
          }
        })
      },
      { name: key, namespace: 'userAnswers' }
    )
  }

  addGroupAnswersStream() {
    if (this.hasStream('groupAnswers')) return

    this.addStream(
      getRoomStateAnswersForGroup(this.repository, {
        roomId: this.meeting.roomId,
      }),
      (answers) => {
        this.meeting.roomStateAnswersForGroup.replaceModels(answers)
      },
      {
        name: 'groupAnswers',
      }
    )
  }

  addRoomStateFeedbackStream() {
    if (this.hasStream('feedback')) return

    this.addStream(
      getRoomStateFeedback(this.repository, {
        roomId: this.meeting.roomId,
        userId: this.currentUser.uid,
      }),
      (feedback) => {
        this.meeting.roomStateFeedback.replaceModel(feedback)
      },
      {
        name: 'feedback',
      }
    )
  }

  addRoomStateSummaryStream() {
    if (this.hasStream('summary')) return

    this.addStream(
      getRoomStateSummary(this.repository, {
        roomId: this.meeting.roomId,
      }),
      (summary) => {
        this.meeting.roomStateSummary.replaceModel(summary)
      },
      {
        name: 'summary',
      }
    )
  }

  addRoomStateCollaborationDataStream() {
    if (this.meeting.isPreview) {
      // In preview mode, we don't fetch collaboration data from Firestore,
      // but we need to mark it as loaded (empty) so components can render
      this.meeting.collaborationData.changeLoadingState(false)
      return
    }

    if (this.hasStream('collaborationData')) return

    this.addStream(
      getRoomStateCollaborationData(this.repository, {
        roomId: this.meeting.roomId,
      }),
      (collaborationDatas) => {
        this.meeting.collaborationData.replaceModels(collaborationDatas)
      },
      {
        name: 'collaborationData',
      }
    )
  }

  addRolesStream() {
    if (this.hasStream('roles')) return

    this.addStream(
      getSlideDeckRoles(this.repository, {
        slideDeckId: this.meeting.slideDeckId,
      }),
      (roles) => {
        this.meeting.roles.replaceModels(roles)
      },
      {
        name: 'roles',
      }
    )
  }

  addRoomStateEngagementStream() {
    if (this.hasStream('engagement')) return
  }

  private rolesFetched?: string
  async fetchRoles(slideDeckId: string) {
    // guard - only fetch the roles once
    if (this.rolesFetched === slideDeckId) return
    this.rolesFetched = slideDeckId

    try {
      const roles = await fetchSlideDeckRoles(this.meeting.repository, {
        slideDeckId: slideDeckId,
      })

      this.meeting.roles.replaceModels(roles)
    } catch (e) {
      // but if we fail, we should try again next time
      this.rolesFetched = undefined
    }
  }

  addPeerReviewQuestionsStream(roomState: RoomState) {
    if (this.hasStream('peerReviewQuestions')) return

    const { sectionId, assignmentId } = roomState.data
    if (!sectionId || !assignmentId) {
      // No assignment, mark as loaded with empty
      this.meeting.peerReviewQuestions.changeLoadingState(false)
      return
    }

    this.addStream(
      getPeerReviewQuestionsStream(this.repository, {
        sectionId,
        assignmentId,
      }),
      (questions) => {
        this.meeting.peerReviewQuestions.replaceModels(questions)
      },
      {
        name: 'peerReviewQuestions',
      }
    )
  }

  private slideDeckFetched?: string
  async fetchSlideDeck(slideDeckId: string) {
    // guard - only fetch the slide deck once
    if (this.slideDeckFetched === slideDeckId) return
    this.slideDeckFetched = slideDeckId

    try {
      const slideDeck = await fetchSlideDeck(this.meeting.repository, {
        slideDeckId: slideDeckId,
      })

      this.meeting.slideDeck.replaceModel(slideDeck)
    } catch (e) {
      // but if we fail, we should try again next time
      this.slideDeckFetched = undefined
    }
  }

  private slidesFetched?: string
  async fetchSlides(slideDeckId: string) {
    // guard - only fetch the slides once
    if (this.slidesFetched === slideDeckId) return
    this.slidesFetched = slideDeckId

    try {
      const slides = await fetchSlides(this.meeting.repository, {
        slideDeckId: slideDeckId,
      })

      this.meeting.slideDeckSlides.replaceModels(slides)
      // this.meeting.slides = slides.map((model, index) =>
      //   this.resolveSlide(model, index)
      // )
    } catch (e) {
      console.error(e)
      // but if we fail, we should try again next time
      this.slidesFetched = undefined
    }
  }

  private exhibitsFetched?: string
  async fetchExhibits(slideDeckId: string) {
    // guard - only fetch the exhibits  once
    if (this.exhibitsFetched === slideDeckId) return
    this.exhibitsFetched = slideDeckId

    try {
      const exhibits = await fetchSlideDeckExhibits(this.meeting.repository, {
        slideDeckId: slideDeckId,
      })

      this.meeting._slideDeckExhibits.replaceModels(exhibits)
    } catch (e) {
      // but if we fail, we should try again next time
      this.exhibitsFetched = undefined
    }
  }

  private questionsFetched?: string
  async fetchQuestions(slideDeckId: string) {
    // guard - only fetch the exhibits  once
    if (this.questionsFetched === slideDeckId) return
    this.questionsFetched = slideDeckId

    try {
      const questions = await fetchSlideQuestions(this.meeting.repository, {
        slideDeckId: slideDeckId,
      })

      this.meeting.slideDeckQuestions.replaceModels(questions)

      // TODO: here subscribe to answers for each question
    } catch (e) {
      // but if we fail, we should try again next time
      this.questionsFetched = undefined
    }
  }

  private assignmentFetched?: string
  async fetchAssignment(
    assignmentId: string | undefined,
    sectionId: string | undefined
  ) {
    if (!assignmentId || !sectionId) return
    if (this.assignmentFetched === assignmentId) return
    this.assignmentFetched = assignmentId

    try {
      const assignment = await fetchSectionAssignment(this.meeting.repository, {
        assignmentId,
        sectionId,
      })
      this.meeting.assignment.replaceModel(assignment)
    } catch (e) {
      this.assignmentFetched = undefined
    }
  }
}
